import type { ReactNode } from 'react';
import { formatMoney, formatMoneyAbsolute } from '@/lib/utils';
import type {
  BalanceTimePoint,
  BeneficiaryBreakdownItem,
  CategoryBreakdownItem,
  ContributionFairness,
  MonthlySpendPoint,
  OutstandingSummary,
  SettlementProgress,
} from '@/lib/types';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { useMemo, useState } from 'react';

function niceAxisMax(value: number): number {
  if (value <= 0) return 5;
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const dt = new Date(Date.UTC(y, m - 1, 1));
  return dt.toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', year: '2-digit' });
}

function formatIsoDateLabel(iso: string): string {
  const d = iso.slice(0, 10);
  const [y, m, day] = d.split('-').map(Number);
  if (!y || !m || !day) return iso;
  const dt = new Date(Date.UTC(y, m - 1, day));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: '2-digit',
  });
}

export function InsightsPanel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
      <div className='mb-4'>
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        {subtitle ? <p className='text-sm text-gray-500 mt-0.5'>{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function EmptyChartState({ message }: { message: string }) {
  return (
    <p className='text-sm text-gray-500 text-center py-8 border border-dashed border-gray-200 rounded-md'>
      {message}
    </p>
  );
}

/** 1. Category spend breakdown — horizontal bars */
export function CategoryBreakdownChart({
  items,
  metric,
}: {
  items: CategoryBreakdownItem[];
  metric: 'household' | 'my_share';
}) {
  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        value: metric === 'my_share' ? item.my_share_cents : item.amount_cents,
      })),
    [items, metric],
  );
  const max = Math.max(...rows.map((r) => r.value), 0);
  if (rows.length === 0 || max <= 0) {
    return <EmptyChartState message='No category spend in this period.' />;
  }

  return (
    <div className='space-y-3'>
      {rows.map((row) => {
        const pct = max > 0 ? (row.value / max) * 100 : 0;
        return (
          <div key={row.category_id}>
            <div className='flex items-center justify-between gap-2 text-sm mb-1'>
              <span className='font-medium text-gray-800 truncate'>{row.category_name}</span>
              <span className='tabular-nums text-gray-700 shrink-0'>
                {formatMoneyAbsolute(row.value)}
                <span className='text-gray-400 text-xs ml-1'>({row.expense_count})</span>
              </span>
            </div>
            <div className='h-2.5 rounded-full bg-gray-100 overflow-hidden'>
              <div
                className='h-full rounded-full'
                style={{ width: `${pct}%`, backgroundColor: row.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 2. Who paid vs who owes */
export function ContributionFairnessChart({
  fairness,
  myLabel = 'This user',
  otherLabel = 'Coparent',
}: {
  fairness: ContributionFairness;
  myLabel?: string;
  otherLabel?: string;
}) {
  const groups = [
    {
      key: 'purchases',
      label: 'Purchased',
      mine: fairness.my_purchases_cents,
      theirs: fairness.other_purchases_cents,
    },
    {
      key: 'share',
      label: 'Split share',
      mine: fairness.my_share_of_expenses_cents,
      theirs: fairness.other_share_of_expenses_cents,
    },
    {
      key: 'payments',
      label: 'Payments sent',
      mine: fairness.my_payments_cents,
      theirs: fairness.other_payments_cents,
    },
  ];
  const max = Math.max(
    ...groups.flatMap((g) => [g.mine, g.theirs]),
    Math.abs(fairness.amount_owed_cents),
    1,
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap gap-4 text-xs text-gray-600'>
        <span className='inline-flex items-center gap-1.5'>
          <span className='w-2.5 h-2.5 rounded-sm bg-primary' /> {myLabel}
        </span>
        <span className='inline-flex items-center gap-1.5'>
          <span className='w-2.5 h-2.5 rounded-sm bg-amber-500' /> {otherLabel}
        </span>
      </div>
      {groups.map((g) => (
        <div key={g.key}>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2'>
            {g.label}
          </p>
          <div className='space-y-1.5'>
            {[
              { label: myLabel, value: g.mine, color: 'bg-primary' },
              { label: otherLabel, value: g.theirs, color: 'bg-amber-500' },
            ].map((row) => (
              <div key={row.label} className='flex items-center gap-2'>
                <span className='w-20 text-xs text-gray-600 truncate'>{row.label}</span>
                <div className='flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden'>
                  <div
                    className={`h-full rounded-full ${row.color}`}
                    style={{ width: `${(row.value / max) * 100}%` }}
                  />
                </div>
                <span className='w-20 text-right text-xs tabular-nums text-gray-800'>
                  {formatMoneyAbsolute(row.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className='text-sm text-gray-700 pt-2 border-t border-gray-100'>
        Period net:{' '}
        <span className='font-semibold tabular-nums'>
          {fairness.amount_owed_cents === 0
            ? 'Settled'
            : fairness.amount_owed_cents > 0
              ? `${myLabel} owes ${formatMoneyAbsolute(fairness.amount_owed_cents)}`
              : `${otherLabel} owes ${formatMoneyAbsolute(fairness.amount_owed_cents)}`}
        </span>
      </p>
    </div>
  );
}

/** 3. Outstanding reimbursements */
export function OutstandingReimbursementsPanel({
  outstanding,
}: {
  outstanding: OutstandingSummary;
}) {
  const maxAging = Math.max(...outstanding.aging.map((a) => a.remaining_cents), 0);
  if (outstanding.count === 0) {
    return <EmptyChartState message='No outstanding reimbursements.' />;
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap gap-4 text-sm'>
        <div>
          <p className='text-xs text-gray-500'>Open balance</p>
          <p className='text-xl font-semibold tabular-nums text-gray-900'>
            {formatMoneyAbsolute(outstanding.total_remaining_cents)}
          </p>
        </div>
        <div>
          <p className='text-xs text-gray-500'>Expenses</p>
          <p className='text-xl font-semibold tabular-nums text-gray-900'>{outstanding.count}</p>
        </div>
      </div>

      <div className='space-y-2'>
        <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Aging</p>
        {outstanding.aging.map((bucket) => (
          <div key={bucket.bucket}>
            <div className='flex justify-between text-xs text-gray-600 mb-1'>
              <span>
                {bucket.label} · {bucket.count}
              </span>
              <span className='tabular-nums'>{formatMoneyAbsolute(bucket.remaining_cents)}</span>
            </div>
            <div className='h-2 rounded-full bg-gray-100 overflow-hidden'>
              <div
                className='h-full rounded-full bg-rose-500'
                style={{
                  width: `${maxAging > 0 ? (bucket.remaining_cents / maxAging) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className='overflow-x-auto border border-gray-100 rounded-md'>
        <table className='min-w-full text-sm'>
          <thead className='bg-gray-50 text-left text-xs text-gray-500'>
            <tr>
              <th className='px-3 py-2 font-semibold'>Expense</th>
              <th className='px-3 py-2 font-semibold'>Age</th>
              <th className='px-3 py-2 font-semibold text-right'>Remaining</th>
            </tr>
          </thead>
          <tbody>
            {outstanding.items.map((item) => (
              <tr key={item.expense_id} className='border-t border-gray-100'>
                <td className='px-3 py-2'>
                  <div className='font-medium text-gray-900 truncate max-w-[14rem]'>{item.name}</div>
                  <div className='text-xs text-gray-500'>
                    {formatIsoDateLabel(item.purchase_date)} · #{item.expense_id}
                  </div>
                </td>
                <td className='px-3 py-2 text-gray-700 tabular-nums'>{item.age_days}d</td>
                <td className='px-3 py-2 text-right tabular-nums font-medium text-gray-900'>
                  {formatMoneyAbsolute(item.remaining_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 4. Balance over time — line chart */
export function BalanceOverTimeChart({ points }: { points: BalanceTimePoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const chartWidth = 640;
  const chartHeight = 220;
  const padding = { top: 20, right: 16, bottom: 36, left: 52 };
  const innerW = chartWidth - padding.left - padding.right;
  const innerH = chartHeight - padding.top - padding.bottom;

  const values = points.map((p) => p.amount_owed_cents);
  const maxAbs = Math.max(...values.map((v) => Math.abs(v)), 1);
  const axisMax = niceAxisMax(maxAbs / 100) * 100;

  if (points.length === 0) {
    return <EmptyChartState message='Not enough history for a balance trend.' />;
  }

  const coords = points.map((p, i) => {
    const x =
      points.length <= 1
        ? padding.left + innerW / 2
        : padding.left + (i / (points.length - 1)) * innerW;
    const y = padding.top + innerH / 2 - (p.amount_owed_cents / axisMax) * (innerH / 2);
    return { ...p, x, y };
  });

  const pathD =
    coords.length >= 2
      ? coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ')
      : '';

  const zeroY = padding.top + innerH / 2;

  return (
    <div>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className='w-full h-auto'
        role='img'
        aria-label='Balance over time'
      >
        <line
          x1={padding.left}
          x2={chartWidth - padding.right}
          y1={zeroY}
          y2={zeroY}
          stroke='#E5E7EB'
          strokeDasharray='4 4'
        />
        <text x={4} y={padding.top + 4} className='fill-gray-400' fontSize={10}>
          {formatMoney(axisMax)}
        </text>
        <text x={4} y={zeroY + 3} className='fill-gray-400' fontSize={10}>
          $0
        </text>
        <text x={4} y={chartHeight - padding.bottom + 4} className='fill-gray-400' fontSize={10}>
          {formatMoney(-axisMax)}
        </text>
        {pathD ? (
          <path d={pathD} fill='none' stroke='#018B9C' strokeWidth={2.5} strokeLinejoin='round' />
        ) : null}
        {coords.map((c, i) => (
          <circle
            key={c.date}
            cx={c.x}
            cy={c.y}
            r={hover === i ? 5 : 3.5}
            fill='#018B9C'
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        {coords.map((c, i) =>
          i === 0 || i === coords.length - 1 || hover === i ? (
            <text
              key={`lbl-${c.date}`}
              x={c.x}
              y={chartHeight - 8}
              textAnchor='middle'
              className='fill-gray-500'
              fontSize={10}
            >
              {formatIsoDateLabel(c.date)}
            </text>
          ) : null,
        )}
        {hover != null && coords[hover] ? (
          <BarChartHoverLabel
            x={coords[hover].x}
            barTopY={Math.min(coords[hover].y, zeroY) - 4}
            text={formatMoney(coords[hover].amount_owed_cents)}
            minX={padding.left + 24}
            maxX={chartWidth - padding.right - 24}
          />
        ) : null}
      </svg>
      <p className='text-xs text-gray-500 mt-1'>
        Positive = this user owes coparent. Negative = coparent owes this user.
      </p>
    </div>
  );
}

/** 5. Spend by beneficiary */
export function BeneficiaryBreakdownChart({
  items,
  metric,
}: {
  items: BeneficiaryBreakdownItem[];
  metric: 'household' | 'my_share';
}) {
  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        value: metric === 'my_share' ? item.my_share_cents : item.amount_cents,
        key: item.beneficiary_id ?? 'general',
      })),
    [items, metric],
  );
  const max = Math.max(...rows.map((r) => r.value), 0);
  if (rows.length === 0 || max <= 0) {
    return <EmptyChartState message='No beneficiary spend in this period.' />;
  }

  const palette = ['#018B9C', '#F59E0B', '#8B5CF6', '#10B981', '#EF4444', '#06B6D4'];

  return (
    <div className='space-y-3'>
      {rows.map((row, idx) => {
        const pct = (row.value / max) * 100;
        return (
          <div key={row.key}>
            <div className='flex items-center justify-between gap-2 text-sm mb-1'>
              <span className='font-medium text-gray-800 truncate'>
                {row.name}
                {row.type ? (
                  <span className='ml-1 text-xs font-normal text-gray-400'>{row.type}</span>
                ) : null}
              </span>
              <span className='tabular-nums text-gray-700 shrink-0'>
                {formatMoneyAbsolute(row.value)}
              </span>
            </div>
            <div className='h-2.5 rounded-full bg-gray-100 overflow-hidden'>
              <div
                className='h-full rounded-full'
                style={{ width: `${pct}%`, backgroundColor: palette[idx % palette.length] }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 6. Monthly spend trend — stacked bars */
export function MonthlySpendChart({ points }: { points: MonthlySpendPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const chartWidth = 640;
  const chartHeight = 220;
  const padding = { top: 24, right: 12, bottom: 36, left: 48 };
  const innerW = chartWidth - padding.left - padding.right;
  const innerH = chartHeight - padding.top - padding.bottom;
  const max = Math.max(...points.map((p) => p.total_cents), 0);
  const axisMax = niceAxisMax(max / 100) * 100 || 100;

  if (points.length === 0 || max <= 0) {
    return <EmptyChartState message='No monthly spend in this period.' />;
  }

  const barGap = 8;
  const barWidth = Math.max(8, (innerW - barGap * (points.length - 1)) / points.length);

  return (
    <div>
      <div className='flex flex-wrap gap-4 text-xs text-gray-600 mb-2'>
        <span className='inline-flex items-center gap-1.5'>
          <span className='w-2.5 h-2.5 rounded-sm bg-primary' /> One-off
        </span>
        <span className='inline-flex items-center gap-1.5'>
          <span className='w-2.5 h-2.5 rounded-sm bg-sky-300' /> Recurring
        </span>
      </div>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className='w-full h-auto'
        role='img'
        aria-label='Monthly spend'
      >
        {points.map((p, i) => {
          const x = padding.left + i * (barWidth + barGap);
          const oneH = (p.one_off_cents / axisMax) * innerH;
          const recH = (p.recurring_cents / axisMax) * innerH;
          const totalH = oneH + recH;
          const baseY = padding.top + innerH;
          return (
            <g
              key={p.month}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <rect
                x={x}
                y={baseY - oneH}
                width={barWidth}
                height={oneH}
                className='fill-primary'
                rx={2}
              />
              <rect
                x={x}
                y={baseY - totalH}
                width={barWidth}
                height={recH}
                className='fill-sky-300'
                rx={2}
              />
              <text
                x={x + barWidth / 2}
                y={chartHeight - 10}
                textAnchor='middle'
                className='fill-gray-500'
                fontSize={10}
              >
                {formatMonthLabel(p.month)}
              </text>
              {hover === i ? (
                <BarChartHoverLabel
                  x={x + barWidth / 2}
                  barTopY={baseY - totalH}
                  text={formatMoneyAbsolute(p.total_cents)}
                  minX={padding.left + 20}
                  maxX={chartWidth - padding.right - 20}
                />
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** 7. Settlement progress */
export function SettlementProgressPanel({ progress }: { progress: SettlementProgress }) {
  const pct = Math.round(progress.progress_ratio * 100);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress.progress_ratio);

  return (
    <div className='flex flex-col sm:flex-row items-center gap-6'>
      <div className='relative w-36 h-36 shrink-0'>
        <svg viewBox='0 0 140 140' className='w-full h-full -rotate-90'>
          <circle
            cx='70'
            cy='70'
            r={radius}
            fill='none'
            stroke='#E5E7EB'
            strokeWidth='12'
          />
          <circle
            cx='70'
            cy='70'
            r={radius}
            fill='none'
            stroke='#018B9C'
            strokeWidth='12'
            strokeLinecap='round'
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className='absolute inset-0 flex flex-col items-center justify-center'>
          <span className='text-2xl font-semibold tabular-nums text-gray-900'>{pct}%</span>
          <span className='text-xs text-gray-500'>settled</span>
        </div>
      </div>
      <dl className='grid grid-cols-1 gap-3 text-sm w-full'>
        <div className='flex justify-between gap-4 border-b border-gray-100 pb-2'>
          <dt className='text-gray-500'>Reimbursable created</dt>
          <dd className='font-semibold tabular-nums text-gray-900'>
            {formatMoneyAbsolute(progress.reimbursable_created_cents)}
          </dd>
        </div>
        <div className='flex justify-between gap-4 border-b border-gray-100 pb-2'>
          <dt className='text-gray-500'>Payments recorded</dt>
          <dd className='font-semibold tabular-nums text-gray-900'>
            {formatMoneyAbsolute(progress.payments_recorded_cents)}
          </dd>
        </div>
        <div className='flex justify-between gap-4'>
          <dt className='text-gray-500'>Still open (period expenses)</dt>
          <dd className='font-semibold tabular-nums text-gray-900'>
            {formatMoneyAbsolute(progress.remaining_open_cents)}
          </dd>
        </div>
      </dl>
    </div>
  );
}
